'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { Check, Receipt } from 'lucide-react'
import { CountUp } from '@/components/ui/count-up'
import { EASE, SPRING } from '@/lib/motion'

// Motion Wave D set-piece for /orders: the UGC-attributed revenue ledger
// mock, extracted verbatim from the page so the server component stays a
// server component. The figure settles up once when in view, ledger rows
// fade up with a short stagger, the surface chips pop on a spring after
// their row, and the per-row order counts count up. Revenue strings are
// formatted text ("$7,821"), so they reveal with their row untouched: the
// same final text always renders. Plays once; reduced motion renders the
// final state instantly.
//
// Clearly representative sample rows. Product names + prices are the
// canonical Idukki marketing-shot SKUs from CLAUDE.md. The order count,
// revenue and AOV figures below are illustrative sample data, NOT a real
// customer's numbers, and are labelled as such on the surface.
const LEDGER: Array<{
  product: string
  price: string
  source: string
  via: 'Reel' | 'Gallery' | 'Story' | 'Wall'
  orders: number
  revenue: string
}> = [
  { product: 'Airlift Overcoat Brown',          price: '$190.76', source: '@maya.k',     via: 'Reel',    orders: 41, revenue: '$7,821' },
  { product: 'WROGN Men Silver-Toned Watch',    price: '$24.76',  source: 'PDP gallery', via: 'Gallery', orders: 88, revenue: '$2,179' },
  { product: 'White Sweater Green Stripes',     price: '$110.76', source: '@liv.j',      via: 'Story',   orders: 23, revenue: '$2,547' },
  { product: 'Airlift High-Waist Suit Up Shorts', price: '$64.76', source: 'Home wall',  via: 'Wall',    orders: 36, revenue: '$2,331' },
  { product: 'Women Oval Sunglasses',           price: '$14.72',  source: '@iris.skin',  via: 'Reel',    orders: 52, revenue: '$765' },
]

export function OrdersLedgerMotion() {
  const ref = React.useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const done = inView // reduced motion jumps straight to the final state

  return (
    <LazyMotion features={domAnimation}>
      <m.figure
        ref={ref}
        className="mt-12 overflow-hidden rounded-2xl border bg-card shadow-sm"
        initial={reduce ? false : { opacity: 0, y: 16 }}
        animate={done ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.45, ease: EASE }}
      >
        <figcaption className="flex flex-wrap items-center justify-between gap-3 border-b bg-secondary/40 px-5 py-4">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-primary" aria-hidden="true" />
            <span className="font-display text-sm font-semibold">UGC-attributed orders</span>
            <span className="rounded-full border bg-card px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Sample data
            </span>
          </div>
          <span className="text-xs text-muted-foreground">Last 30 days · attribution window 7 days</span>
        </figcaption>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b text-[11px] uppercase tracking-wider text-muted-foreground">
                <th scope="col" className="px-5 py-3 font-semibold">Product</th>
                <th scope="col" className="px-5 py-3 font-semibold">Source</th>
                <th scope="col" className="px-5 py-3 font-semibold">Surface</th>
                <th scope="col" className="px-5 py-3 text-right font-semibold">Orders</th>
                <th scope="col" className="px-5 py-3 text-right font-semibold">Attributed revenue</th>
              </tr>
            </thead>
            <tbody>
              {LEDGER.map((row, i) => {
                const rowDelay = 0.15 + i * 0.07
                return (
                  <m.tr
                    key={row.product}
                    className="border-b last:border-0 transition-colors hover:bg-secondary/30"
                    initial={reduce ? false : { opacity: 0, y: 12 }}
                    animate={done ? { opacity: 1, y: 0 } : undefined}
                    transition={{ duration: 0.35, ease: EASE, delay: reduce ? 0 : rowDelay }}
                  >
                    <th scope="row" className="px-5 py-4 font-medium text-foreground">
                      {row.product}
                      <span className="ml-2 font-mono text-[11px] font-normal text-muted-foreground">{row.price}</span>
                    </th>
                    <td className="px-5 py-4 text-muted-foreground">{row.source}</td>
                    <td className="px-5 py-4">
                      <m.span
                        className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary"
                        initial={reduce ? false : { opacity: 0, scale: 0.8 }}
                        animate={done ? { opacity: 1, scale: 1 } : undefined}
                        transition={reduce ? { duration: 0 } : { ...SPRING, delay: rowDelay + 0.12 }}
                      >
                        {row.via}
                      </m.span>
                    </td>
                    <td className="px-5 py-4 text-right font-mono tabular-nums text-foreground">
                      <CountUp value={row.orders} duration={0.9} />
                    </td>
                    <td className="px-5 py-4 text-right font-mono font-semibold tabular-nums text-foreground">{row.revenue}</td>
                  </m.tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-3 border-t bg-secondary/40 px-5 py-3 text-xs text-muted-foreground">
          <span>5 of 142 sources shown</span>
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
            Reconciled to order webhooks
          </span>
        </div>
      </m.figure>
    </LazyMotion>
  )
}
